import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { useMemo, useState } from 'react';

export type EffectiveUserRow = {
  id: string;
  displayName: string;
  userPrincipalName: string;
  // false when the user's Entra account is disabled — flagged for cleanup review.
  accountEnabled: boolean;
  // Assigned directly to the ACL (policy drift) rather than via a Role group.
  direct: boolean;
  // Display names of the Role group(s) granting this user access.
  viaRoles: string[];
  // Would be granted access by a queued (not-yet-submitted) role add — shown green.
  pending: boolean;
  // Would lose access if the queued role removals are submitted — shown red.
  removed: boolean;
};

type Props = {
  rows?: EffectiveUserRow[];
  isFetching?: boolean;
};

// The effective access list: everyone who can reach the ACL's resource. Each row is
// attributed to how they get access (Direct, or the specific Role group), searchable,
// and scrollable. Users a queued role-add would newly grant access to show in green.
export default function ACLEffectiveUserList({ rows, isFetching }: Props) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (rows ?? []).filter(
      (row) =>
        !q ||
        row.displayName.toLowerCase().includes(q) ||
        row.userPrincipalName.toLowerCase().includes(q) ||
        row.viaRoles.some((role) => role.toLowerCase().includes(q))
    );
    // Staged changes first — additions (green), then removals (red) — then the rest,
    // alphabetical within each group.
    const rank = (row: EffectiveUserRow) => (row.pending ? 0 : row.removed ? 1 : 2);
    return matches.sort((a, b) => rank(a) - rank(b) || a.displayName.localeCompare(b.displayName));
  }, [rows, query]);

  const currentCount = (rows ?? []).filter((row) => !row.pending).length;
  const pendingCount = (rows ?? []).filter((row) => row.pending).length;
  const removedCount = (rows ?? []).filter((row) => row.removed).length;
  const directCount = (rows ?? []).filter((row) => row.direct && !row.pending).length;

  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-4 flex flex-col max-h-[calc(100vh-16rem)]">
      <div className="flex flex-row flex-wrap items-center gap-2 pb-1">
        <span className="font-bold">Effective users</span>
        <span className="badge badge-ghost">{currentCount}</span>
        {pendingCount > 0 && <span className="badge badge-success">+{pendingCount}</span>}
        {removedCount > 0 && <span className="badge badge-error">-{removedCount}</span>}
        <input
          type="text"
          className="input input-bordered input-sm w-56 ml-auto focus:outline-none"
          placeholder="Search users"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {directCount > 0 && (
        <div className="text-warning text-xs pb-2">
          {directCount} assigned directly (not via a Role group).
        </div>
      )}
      <div className="border border-base-200 rounded-lg overflow-hidden flex-1 min-h-0">
        <ul className="divide-y divide-base-200 w-full h-full overflow-y-auto">
          {isFetching ? (
            <li className="p-2 text-sm opacity-60">Loading users...</li>
          ) : filtered.length === 0 ? (
            <li className="p-2 text-sm opacity-60">
              {rows && rows.length > 0 ? 'No matching users.' : 'No users have access to this ACL.'}
            </li>
          ) : (
            filtered.map((row) => (
              <li
                key={row.pending ? row.id + '_pending' : row.id}
                className={classNames('p-2 flex items-center justify-between gap-3', {
                  'text-error-content bg-error': row.removed,
                  'text-success-content bg-success': row.pending,
                })}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1 min-w-0">
                    <span className="truncate">{row.displayName}</span>
                    {!row.accountEnabled && (
                      <span title="Account disabled" className="shrink-0 inline-flex">
                        <ExclamationTriangleIcon className="h-4 w-4 text-warning" />
                      </span>
                    )}
                  </div>
                  <div className="text-xs opacity-70 truncate">{row.userPrincipalName}</div>
                </div>
                <div className="flex items-center gap-2 min-w-0 max-w-[55%] justify-end">
                  {row.direct && <span className="badge badge-warning shrink-0">Direct</span>}
                  {row.viaRoles.length > 0 && (
                    <span
                      className="text-xs opacity-70 truncate min-w-0"
                      title={row.viaRoles.join(', ')}
                    >
                      {row.viaRoles[0]}
                      {row.viaRoles.length > 1 ? ` +${row.viaRoles.length - 1}` : ''}
                    </span>
                  )}
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
